import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { 
    LayoutDashboard, 
    UserPlus, 
    Gift, 
    Users, 
    Settings, 
    LogOut,
    Building2,
    ChevronRight,
    Menu,
    X,
    Mail,
    BarChart3,
    HandCoins,
    Briefcase,
    ScrollText,
    FolderOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useState } from 'react';

const NavItem = ({ to, icon: Icon, label, active, onClick }) => (
    <Link
        to={to}
        onClick={onClick}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            active 
                ? 'bg-slate-100 text-slate-900 font-medium' 
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
        <Icon size={20} strokeWidth={1.5} />
        <span>{label}</span>
    </Link>
);

export const DashboardLayout = ({ children }) => {
    const { user, logout, isSuperadmin, isOrgAdmin, isSalesRep } = useAuth();
    const { tenant, organizationCode } = useTenant();
    const location = useLocation();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        if (organizationCode) {
            navigate(`/o/${organizationCode}/login`);
        } else {
            navigate('/superadmin/login');
        }
    };

    const baseUrl = isSuperadmin ? '/superadmin' : `/o/${organizationCode}`;
    
    // Customer navigation - simplified
    const customerNav = [
        { to: `${baseUrl}/dashboard`, icon: LayoutDashboard, label: 'Übersicht' },
        { to: `${baseUrl}/submit-referral`, icon: UserPlus, label: 'Empfehlung eintragen' },
        { to: `${baseUrl}/rewards`, icon: Gift, label: 'Prämien' },
        { to: `${baseUrl}/terms`, icon: ScrollText, label: 'Teilnahmebedingungen' },
    ];

    // Sales Rep navigation
    const salesRepNav = [
        { to: `${baseUrl}/sales`, icon: LayoutDashboard, label: 'Dashboard' },
    ];

    // Admin navigation - with invitations and sales reps
    const adminNav = [
        { to: `${baseUrl}/admin/invitations`, icon: Mail, label: 'Einladungen' },
        { to: `${baseUrl}/admin/referrals`, icon: Users, label: 'Empfehlungen' },
        { to: `${baseUrl}/admin/customers`, icon: UserPlus, label: 'Kunden' },
        { to: `${baseUrl}/admin/rewards`, icon: Gift, label: 'Prämien' },
        { to: `${baseUrl}/admin/reward-sections`, icon: FolderOpen, label: 'Prämien-Kategorien' },
        { to: `${baseUrl}/admin/redemptions`, icon: HandCoins, label: 'Einlösungen' },
        { to: `${baseUrl}/admin/sales-reps`, icon: Briefcase, label: 'Vertriebler' },
        { to: `${baseUrl}/admin/analytics`, icon: BarChart3, label: 'Analytik' },
        { to: `${baseUrl}/admin/settings`, icon: Settings, label: 'Einstellungen' },
    ];

    const superadminNav = [
        { to: '/superadmin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/superadmin/organizations', icon: Building2, label: 'Organisationen' },
    ];

    const navItems = isSuperadmin 
        ? superadminNav 
        : isOrgAdmin
            ? adminNav
            : isSalesRep
                ? salesRepNav
                : customerNav;

    const closeMobileMenu = () => setMobileMenuOpen(false);

    return (
        <div className="dashboard-layout">
            {/* Mobile Header */}
            <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b border-slate-200 z-40 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="p-2 hover:bg-slate-100 rounded-lg"
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                    <span 
                        className="font-semibold"
                        style={{ color: tenant?.primary_color || '#0f172a' }}
                    >
                        {tenant?.name || 'Admin Panel'}
                    </span>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div 
                    className="lg:hidden fixed inset-0 bg-black/50 z-40"
                    onClick={closeMobileMenu}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                dashboard-sidebar fixed lg:sticky top-0 h-screen z-50
                transform lg:transform-none transition-transform duration-200
                ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="p-6 border-b border-slate-200">
                        {tenant?.logo ? (
                            <img src={tenant.logo} alt={tenant.name} className="h-8 object-contain" />
                        ) : (
                            <h1 
                                className="text-xl font-bold"
                                style={{ color: tenant?.primary_color || '#0f172a' }}
                            >
                                {tenant?.name || 'Admin Panel'}
                            </h1>
                        )}
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                        {navItems.map((item) => (
                            <NavItem
                                key={item.to}
                                to={item.to}
                                icon={item.icon}
                                label={item.label}
                                active={location.pathname === item.to}
                                onClick={closeMobileMenu}
                            />
                        ))}
                    </nav>

                    {/* User section */}
                    <div className="p-4 border-t border-slate-200">
                        <div className="flex items-center gap-3 px-4 py-3">
                            <div 
                                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                                style={{ backgroundColor: tenant?.primary_color || '#0f172a' }}
                            >
                                {user?.name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            className="w-full justify-start mt-2 text-slate-600 hover:text-red-600 hover:bg-red-50"
                            onClick={handleLogout}
                            data-testid="logout-btn"
                        >
                            <LogOut size={18} className="mr-2" />
                            Abmelden
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Main Content */}
            <main className="dashboard-main pt-16 lg:pt-0">
                <div className="p-6 lg:p-8 max-w-7xl mx-auto">
                    {children}
                </div>
            </main>
        </div>
    );
};
